import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import AvatarCrop from '../components/AvatarCrop';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { useLibrary } from '../context/LibraryContext';
import { useBooks } from '../context/BooksContext';
import { useSocial } from '../context/SocialContext';
import { useTheme } from '../context/ThemeContext';
import { usePlumes } from '../components/Plumes';
import { collection, query, where, orderBy, onSnapshot, addDoc, deleteDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import AuthorWallet from '../components/AuthorWallet';
import Badges from '../components/Badges';
import ReadingStreak from '../components/ReadingStreak';
import ReadingStats from '../components/ReadingStats';
import ReaderLevel from '../components/ReaderLevel';
import WeeklyChallenges from '../components/WeeklyChallenges';
import InviteFriend from '../components/InviteFriend';
import { buyPlumes, subscribePremium, cancelPremium, deleteAccount } from '../utils/stripe';
import {
  IoLogOut, IoPencil, IoCheckmark, IoClose, IoMoon, IoSunny,
  IoCamera, IoSend, IoTrash, IoHeart, IoChatbubble, IoRocket,
  IoNewspaper, IoSettings, IoStar, IoShield, IoSparkles
} from 'react-icons/io5';
import './Profile.css';

const AVATAR_COLORS = [
  'linear-gradient(135deg, #c8956c, #d4a574)',
  'linear-gradient(135deg, #c27a7a, #d49a9a)',
  'linear-gradient(135deg, #7a9e7e, #9ab89e)',
  'linear-gradient(135deg, #8b6f8e, #a88dab)',
  'linear-gradient(135deg, #7a9ab5, #9ab5cc)',
  'linear-gradient(135deg, #d4a574, #e8c4a0)',
];

const PLUME_PACKS = [
  { id: 'p50', amount: 50, price: '0,99€', emoji: '🪶' },
  { id: 'p200', amount: 200, price: '4,49€', emoji: '🪶🪶' },
  { id: 'p500', amount: 500, price: '10,99€', emoji: '🪶🪶🪶' },
  { id: 'p1500', amount: 1500, price: '32,99€', emoji: '✨🪶✨' },
];

const PREMIUM_PERKS = [
  { icon: IoShield, text: 'Aucune publicité' },
  { icon: IoSparkles, text: 'Stats avancées sur tes œuvres' },
  { icon: IoStar, text: 'Badge Premium visible' },
  { icon: IoRocket, text: 'Accès anticipé aux nouveautés' },
];

function timeAgo(d) {
  if (!d) return '';
  const date = d.toDate ? d.toDate() : new Date(d);
  const s = (Date.now() - date.getTime()) / 1000;
  if (s < 60) return "À l'instant";
  if (s < 3600) return Math.floor(s / 60) + ' min';
  if (s < 86400) return Math.floor(s / 3600) + ' h';
  if (s < 604800) return Math.floor(s / 86400) + ' j';
  return date.toLocaleDateString('fr-FR');
}

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useUser();
  const { library } = useLibrary();
  const { books } = useBooks();
  const { followers, following } = useSocial();
  const { plumes } = usePlumes();
  const myBooks = books.filter(b => b.authorId === user.authorId);
  const { theme, toggleTheme } = useTheme();

  const [tab, setTab] = useState('profil');
  const [editing, setEditing] = useState(false);
const [editForm, setEditForm] = useState({ name: user.name, username: user.username || '', bio: user.bio || '', avatarColor: user.avatarColor || AVATAR_COLORS[0] });
  const [showPlumeShop, setShowPlumeShop] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
const [showFollowing, setShowFollowing] = useState(false);
const [followerProfiles, setFollowerProfiles] = useState([]);
const [followingProfiles, setFollowingProfiles] = useState([]);
const [loadingProfiles, setLoadingProfiles] = useState(false);
const [cropImage, setCropImage] = useState(null);
const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [paymentPopup, setPaymentPopup] = useState(() => {
    const payment = sessionStorage.getItem('plumly_payment');
    if (payment) {
      sessionStorage.removeItem('plumly_payment');
      if (payment === 'success') return 'plumes';
      if (payment === 'premium_success') return 'premium';
      if (payment === 'support_success') return 'support';
      if (payment === 'cancel') return 'cancel';
    }
    return null;
  });

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(query(collection(db, 'posts'), where('authorId', '==', user.id), orderBy('createdAt', 'desc')), snap => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  const loadFollowerProfiles = async () => {
  if (followerProfiles.length > 0) { setShowFollowers(true); return; }
  setLoadingProfiles(true);
  setShowFollowers(true);
  const profiles = [];
  for (const id of followers) {
    try {
      const snap = await getDoc(doc(db, 'users', id));
      if (snap.exists()) profiles.push({ id, ...snap.data() });
    } catch (e) {}
  }
  setFollowerProfiles(profiles);
  setLoadingProfiles(false);
};

const loadFollowingProfiles = async () => {
  if (followingProfiles.length > 0) { setShowFollowing(true); return; }
  setLoadingProfiles(true);
  setShowFollowing(true);
  const profiles = [];
  for (const id of following) {
    try {
      const snap = await getDoc(doc(db, 'users', id));
      if (snap.exists()) profiles.push({ id, ...snap.data() });
    } catch (e) {}
  }
  setFollowingProfiles(profiles);
  setLoadingProfiles(false);
};

  const publish = async () => {
    if (!newPost.trim() || posting) return;
    setPosting(true);
    try {
      await addDoc(collection(db, 'posts'), {
        content: newPost.trim(), authorId: user.id, authorName: user.name,
        authorAvatar: user.avatarImage || null, authorAvatarColor: user.avatarColor || AVATAR_COLORS[0],
        likes: 0, comments: 0, createdAt: serverTimestamp(),
      });
      setNewPost(''); setShowCompose(false);
    } catch (e) {}
    setPosting(false);
  };

  const deletePost = async (id) => { if (window.confirm('Supprimer ?')) await deleteDoc(doc(db, 'posts', id)); };
  const save = async () => { await updateUser(editForm); setEditing(false); };
const cancel = () => { setEditForm({ name: user.name, username: user.username || '', bio: user.bio || '', avatarColor: user.avatarColor || AVATAR_COLORS[0] }); setEditing(false); };


const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image trop lourde (max 5 Mo)');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Fichier non supporté. Choisis une image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCropImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = async (blob) => {
    setCropImage(null);
    setUploadingAvatar(true);
    try {
      const storageRef = ref(storage, `avatars/${user.id}.jpg`);
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      await updateUser({ avatarImage: url });
    } catch (e) {
      console.error('Erreur upload avatar:', e);
      alert('Erreur lors de l\'upload. Réessaie.');
    }
    setUploadingAvatar(false);
  };

  const handleCancelPremium = async () => {
    if (!window.confirm('Tu es sûr de vouloir annuler ton abonnement Premium ?\n\nTu garderas l\'accès jusqu\'à la fin de ta période en cours.')) return;
    setCancelling(true);
    try {
      await cancelPremium();
      alert('Abonnement annulé. Tu gardes l\'accès Premium jusqu\'à la fin de la période en cours.');
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Erreur lors de l\'annulation. Réessaie.');
    }
    setCancelling(false);
  };

  const isPremium = user?.premium;

  return (
    <div className="page profile-page">
            {cropImage && (
        <AvatarCrop
          imageUrl={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropImage(null)}
        />
      )}

      {/* ===== POPUP CONFIRMATION PAIEMENT ===== */}
      <AnimatePresence>
        {paymentPopup && (
          <>
            <motion.div
              className="payment-popup-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPaymentPopup(null)}
            />
            <motion.div
              className="payment-popup"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {paymentPopup === 'plumes' && (
                <>
                  <motion.div className="payment-popup-icon" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', delay: 0.2 }}>🪶</motion.div>
                  <h3 className="payment-popup-title">Plumes reçues !</h3>
                  <p className="payment-popup-text">Ton achat a été confirmé. Tes plumes sont déjà sur ton compte !</p>
                  <p className="payment-popup-sub">Utilise-les pour soutenir tes auteurs préférés ou booster des livres.</p>
                </>
              )}
              {paymentPopup === 'premium' && (
                <>
                  <motion.div className="payment-popup-icon" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', delay: 0.2 }}>⭐</motion.div>
                  <h3 className="payment-popup-title">Bienvenue en Premium !</h3>
                  <p className="payment-popup-text">Tu fais désormais partie de l'élite Plumly. Profite de ton expérience sans pub !</p>
                  <p className="payment-popup-sub">Badge Premium • Zéro pub • Stats avancées • Accès anticipé</p>
                </>
              )}
              {paymentPopup === 'support' && (
                <>
                  <motion.div className="payment-popup-icon" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', delay: 0.2 }}>❤️</motion.div>
                  <h3 className="payment-popup-title">Merci pour ton soutien !</h3>
                  <p className="payment-popup-text">L'auteur recevra ton don très bientôt. Tu fais vivre la création !</p>
                  <p className="payment-popup-sub">85% de ton don va directement à l'auteur.</p>
                </>
              )}
              {paymentPopup === 'cancel' && (
                <>
                  <motion.div className="payment-popup-icon" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}>😊</motion.div>
                  <h3 className="payment-popup-title">Paiement annulé</h3>
                  <p className="payment-popup-text">Pas de souci ! Tu pourras revenir quand tu veux.</p>
                </>
              )}
              <motion.button className="payment-popup-btn" onClick={() => setPaymentPopup(null)} whileTap={{ scale: 0.95 }}>
                {paymentPopup === 'cancel' ? 'Fermer' : 'Super, merci !'}
              </motion.button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== MODALE FOLLOWERS ===== */}
      <AnimatePresence>
        {showFollowers && (
          <>
            <motion.div className="p-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowFollowers(false)} />
<motion.div className="p-modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>
              <div className="p-modal-header">
                <h3>Followers ({followers.length})</h3>
                <button className="p-modal-close" onClick={() => setShowFollowers(false)}><IoClose /></button>
              </div>
              <div className="p-modal-list">
                {loadingProfiles ? (
                  <p className="p-modal-loading">Chargement...</p>
                ) : followerProfiles.length > 0 ? (
                  followerProfiles.map(p => (
<div key={p.id} className="p-modal-user" onClick={() => { setShowFollowing(false); navigate('/author/' + p.authorId); }}>

                      <div className="p-modal-avatar" style={{ background: p.avatarImage ? `url(${p.avatarImage}) center/cover no-repeat` : (p.avatarColor || 'var(--amber)') }}>
                        {!p.avatarImage && (p.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="p-modal-user-info">
                        <strong>{p.name || 'Anonyme'}</strong>
                        {p.username && <span>@{p.username}</span>}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="p-modal-empty">Aucun follower pour le moment</p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== MODALE ABONNEMENTS ===== */}
      <AnimatePresence>
        {showFollowing && (
          <>
            <motion.div className="p-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowFollowing(false)} />
<motion.div className="p-modal" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}>

              <div className="p-modal-header">
                <h3>Abonnements ({following.length})</h3>
                <button className="p-modal-close" onClick={() => setShowFollowing(false)}><IoClose /></button>
              </div>
              <div className="p-modal-list">
                {loadingProfiles ? (
                  <p className="p-modal-loading">Chargement...</p>
                ) : followingProfiles.length > 0 ? (
                  followingProfiles.map(p => (
<div key={p.id} className="p-modal-user" onClick={() => { setShowFollowers(false); navigate('/author/' + p.authorId); }}>

                      <div className="p-modal-avatar" style={{ background: p.avatarImage ? `url(${p.avatarImage}) center/cover no-repeat` : (p.avatarColor || 'var(--amber)') }}>
                        {!p.avatarImage && (p.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="p-modal-user-info">
                        <strong>{p.name || 'Anonyme'}</strong>
                        {p.username && <span>@{p.username}</span>}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="p-modal-empty">Aucun abonnement</p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== HEADER ===== */}
      <motion.div className="p-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {editing ? (
          <div className="p-edit">
            <div className="profile-avatar-wrapper">
              <div className="p-avatar" style={{ background: user.avatarImage ? `url(${user.avatarImage}) center/cover no-repeat` : editForm.avatarColor }}>
                {!user.avatarImage && editForm.name.charAt(0).toUpperCase()}
              </div>
<label className="p-avatar-upload">{uploadingAvatar ? '⏳' : <IoCamera />}<input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarSelect} disabled={uploadingAvatar} /></label>
            </div>
            <div className="p-colors">
              {AVATAR_COLORS.map(c => <button key={c} className={'p-color ' + (editForm.avatarColor === c ? 'p-color-on' : '')} style={{ background: c }} onClick={() => setEditForm({ ...editForm, avatarColor: c })} />)}
            </div>
            <div className="p-field"><label>Nom de plume</label><input className="input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Ton nom d'auteur" /></div>
<div className="p-field">
  <label>Pseudo</label>
  <div className="p-username-edit">
    <span className="p-username-at">@</span>
    <input
      className="input p-username-input"
      value={editForm.username}
      onChange={e => setEditForm({ ...editForm, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase() })}
      placeholder="ton_pseudo"
      maxLength={20}
    />
  </div>
</div>
<div className="p-field"><label>Bio</label><textarea className="input" style={{ minHeight: 80, resize: 'vertical' }} placeholder="Parle-nous de toi..." value={editForm.bio} onChange={e => setEditForm({ ...editForm, bio: e.target.value })} maxLength={200} /></div>
            <div className="p-edit-btns">
              <motion.button className="btn btn-primary" onClick={save} whileTap={{ scale: 0.95 }}><IoCheckmark /> Sauvegarder</motion.button>
              <motion.button className="btn btn-ghost" onClick={cancel} whileTap={{ scale: 0.95 }}><IoClose /> Annuler</motion.button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-header">
              <div className="p-avatar" style={{ background: user.avatarImage ? `url(${user.avatarImage}) center/cover no-repeat` : (user.avatarColor || 'var(--amber)') }}>
                {!user.avatarImage && (user.name?.charAt(0).toUpperCase() || '?')}
              </div>
              <div className="p-info">
                <div className="p-name-row">
                  <h1 className="p-name">{user.name}</h1>
                  {isPremium && <span className="p-premium-tag"><IoStar /> Premium</span>}
                </div>
                {user.accountType && (
                  <div className="p-account-badges">
                    {user.accountType === 'admin' && <span className="p-account-badge p-badge-admin"><IoShield /> Admin</span>}
                    {user.accountType === 'moderator' && <span className="p-account-badge p-badge-moderator"><IoShield /> Modérateur</span>}
                    {user.accountType === 'beta' && <span className="p-account-badge p-badge-beta">🔥 Bêta Testeur</span>}
                    {user.accountType === 'author_verified' && <span className="p-account-badge p-badge-verified"><IoStar /> Auteur Vérifié</span>}
                  </div>
                )}
                {user.username && <p className="p-username">@{user.username}</p>}
                {user.bio && <p className="p-bio">{user.bio}</p>}
                <div className="p-follows">
                  <button type="button" className="p-follows-link" onClick={(e) => { e.stopPropagation(); loadFollowerProfiles(); }}><strong>{followers.length}</strong> followers</button>
                  <span className="p-dot">·</span>
                  <button type="button" className="p-follows-link" onClick={(e) => { e.stopPropagation(); loadFollowingProfiles(); }}><strong>{following.length}</strong> abonnements</button>
                </div>
              </div>
              <motion.button className="p-edit-btn" onClick={() => setEditing(true)} whileTap={{ scale: 0.95 }}><IoPencil /></motion.button>
            </div>
            <div className="p-stats">
              <div className="p-stat"><span className="p-stat-val">{library.length}</span><span className="p-stat-lbl">Biblio</span></div>
              <div className="p-stat-div" />
              <div className="p-stat"><span className="p-stat-val">{myBooks.length}</span><span className="p-stat-lbl">Écrits</span></div>
              <div className="p-stat-div" />
              <div className="p-stat"><span className="p-stat-val">{myBooks.reduce((s, b) => s + (b.likes || 0), 0)}</span><span className="p-stat-lbl">Likes</span></div>
              <div className="p-stat-div" />
              <button type="button" className="p-stat p-stat-clickable" onClick={(e) => { e.stopPropagation(); loadFollowerProfiles(); }}>
                <span className="p-stat-val">{followers.length}</span><span className="p-stat-lbl">Fans</span>
              </button>
            </div>
          </>
        )}
      </motion.div>

      {!editing && (
        <>
          {/* ===== PLUMES + PREMIUM ===== */}
          <div className="p-wallet-row">
            <div className="p-plumes-card" onClick={() => setShowPlumeShop(!showPlumeShop)}>
              <span className="p-plumes-emoji">🪶</span>
              <div className="p-plumes-info">
                <strong>{plumes.toLocaleString('fr-FR')}</strong>
                <span>plumes</span>
              </div>
              <span className="p-plumes-action">{showPlumeShop ? '✕' : '+'}</span>
            </div>

            {!isPremium ? (
              <div className="p-premium-card" onClick={() => setShowPremium(!showPremium)}>
                <IoRocket className="p-premium-icon" />
                <div className="p-premium-info">
                  <strong>Premium</strong>
                  <span>4,99€/mois</span>
                </div>
                <span className="p-premium-action">{showPremium ? '✕' : '→'}</span>
              </div>
            ) : (
              <div className="p-premium-card p-premium-active-card" onClick={() => setShowPremium(!showPremium)}>
                <IoStar className="p-premium-icon-active" />
                <div className="p-premium-info">
                  <strong>Premium</strong>
                  <span>{user.premiumCancelAtEnd ? 'Fin prévue' : 'Actif'}</span>
                </div>
                <span className="p-premium-action-text">{showPremium ? '✕' : '⚙'}</span>
              </div>
            )}
          </div>

          {/* Boutique plumes dépliable */}
          <AnimatePresence>
            {showPlumeShop && (
              <motion.div className="p-plume-shop" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <p className="p-plume-shop-title">Acheter des plumes</p>
                <p className="p-plume-shop-sub">Soutiens tes auteurs préférés et booste des livres</p>
                <div className="p-plume-packs">
                  {PLUME_PACKS.map(p => (
                    <motion.button key={p.id} className="p-plume-pack" whileTap={{ scale: 0.95 }}
                      onClick={() => buyPlumes('price_plumes_' + p.amount)}>
                      <span className="p-pack-emoji">{p.emoji}</span>
                      <strong className="p-pack-amount">{p.amount}</strong>
                      <span className="p-pack-price">{p.price}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Premium dépliable — NON abonné */}
          <AnimatePresence>
            {showPremium && !isPremium && (
              <motion.div className="p-premium-expand" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <div className="p-premium-perks">
                  {PREMIUM_PERKS.map((pk, i) => {
                    const Icon = pk.icon;
                    return <div key={i} className="p-premium-perk"><Icon /><span>{pk.text}</span></div>;
                  })}
                </div>
                <div className="p-premium-plans">
                  <motion.button
                    className={'p-premium-plan ' + (selectedPlan === 'monthly' ? 'p-premium-plan-active' : '')}
                    onClick={() => setSelectedPlan('monthly')}
                    whileTap={{ scale: 0.97 }}
                  >
                    <strong>4,99€</strong><span>/mois</span>
                  </motion.button>
                  <motion.button
                    className={'p-premium-plan p-premium-plan-best ' + (selectedPlan === 'yearly' ? 'p-premium-plan-active' : '')}
                    onClick={() => setSelectedPlan('yearly')}
                    whileTap={{ scale: 0.97 }}
                  >
                    <div className="p-plan-badge">-33%</div>
                    <strong>39,99€</strong><span>/an</span>
                  </motion.button>
                </div>
                <motion.button className="p-premium-subscribe" whileTap={{ scale: 0.97 }}
                  onClick={() => subscribePremium(selectedPlan)}>
                  <IoStar /> S'abonner — {selectedPlan === 'yearly' ? '39,99€/an' : '4,99€/mois'}
                </motion.button>
                <p className="p-premium-note">Annulable à tout moment · Paiement sécurisé Stripe</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Gestion abonnement Premium actif */}
          <AnimatePresence>
            {showPremium && isPremium && (
              <motion.div className="p-premium-expand" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                <div className="p-premium-manage">
                  <div className="p-premium-status">
                    <IoStar className="p-premium-status-icon" />
                    <div>
                      <p className="p-premium-status-title">Abonnement Premium actif</p>
                      <p className="p-premium-status-plan">
                        {user.premiumPlan === 'yearly' ? 'Plan annuel — 39,99€/an' : 'Plan mensuel — 4,99€/mois'}
                      </p>
                      {user.premiumUntil && (
                        <p className="p-premium-status-until">
                          {user.premiumCancelAtEnd
                            ? `Se termine le ${new Date(user.premiumUntil).toLocaleDateString('fr-FR')}`
                            : `Prochain renouvellement le ${new Date(user.premiumUntil).toLocaleDateString('fr-FR')}`
                          }
                        </p>
                      )}
                    </div>
                  </div>

                  {user.premiumCancelAtEnd ? (
                    <div className="p-premium-cancelled-section">
                      <div className="p-premium-cancelled-notice">
                        <p>Ton abonnement ne sera pas renouvelé. Tu gardes l'accès Premium jusqu'à la date indiquée.</p>
                      </div>
                      <motion.button
                        className="p-premium-renew-btn"
                        onClick={() => subscribePremium(user.premiumPlan === 'yearly' ? 'yearly' : 'monthly')}
                        whileTap={{ scale: 0.97 }}
                      >
                        <IoStar /> Se réabonner
                      </motion.button>
                    </div>
                  ) : (
                    <motion.button
                      className="p-premium-cancel-btn"
                      onClick={handleCancelPremium}
                      whileTap={{ scale: 0.97 }}
                      disabled={cancelling}
                    >
                      {cancelling ? 'Annulation...' : 'Annuler mon abonnement'}
                    </motion.button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ===== ONGLETS ===== */}
          <div className="p-tabs">
            <button className={'p-tab ' + (tab === 'profil' ? 'p-tab-on' : '')} onClick={() => setTab('profil')}>
              <IoSettings /> Profil
            </button>
            <button className={'p-tab ' + (tab === 'mur' ? 'p-tab-on' : '')} onClick={() => setTab('mur')}>
              <IoNewspaper /> Mur
              {posts.length > 0 && <span className="p-tab-badge">{posts.length}</span>}
            </button>
          </div>

          {/* ===== TAB PROFIL ===== */}
          {tab === 'profil' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="p-section"><Badges compact /></div>
              <div className="p-section"><ReaderLevel /></div>
              <div className="p-section"><ReadingStreak /><ReadingStats /></div>
              <WeeklyChallenges />
              {myBooks.length > 0 && <AuthorWallet />}
              <Badges />
              <InviteFriend />

              <div className="p-settings">
                <div className="p-theme-row">
                  <div className="p-theme-left">
                    {theme === 'dark' ? <IoMoon style={{ fontSize: 20, color: 'var(--amber)' }} /> : <IoSunny style={{ fontSize: 20, color: 'var(--amber)' }} />}
                    <div><p className="p-theme-label">Apparence</p><p className="p-theme-val">{theme === 'dark' ? 'Sombre' : 'Clair'}</p></div>
                  </div>
                  <button className="p-theme-switch" onClick={toggleTheme}>
                    <motion.div className="p-theme-knob" animate={{ x: theme === 'dark' ? 22 : 0 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                  </button>
                </div>
              </div>

              <div className="p-danger-zone">
                <h4 className="p-danger-title">Zone dangereuse</h4>
                <p className="p-danger-desc">
                  La suppression de ton compte est irréversible. Toutes tes données seront définitivement effacées : profil, livres, messages, plumes, abonnement.
                </p>
                <motion.button
                  className="p-delete-account"
                  onClick={async () => {
                    const confirm1 = window.confirm('⚠️ Tu es sûr de vouloir supprimer ton compte ?\n\nToutes tes données seront définitivement effacées :\n• Ton profil et tes livres\n• Tes messages et commentaires\n• Tes plumes et transactions\n• Ton abonnement Premium\n\nCette action est IRRÉVERSIBLE.');
                    if (!confirm1) return;
                    const confirm2 = window.prompt('Pour confirmer, tape "SUPPRIMER" en majuscules :');
                    if (confirm2 !== 'SUPPRIMER') {
                      alert('Suppression annulée.');
                      return;
                    }
                    setDeleting(true);
                    try {
                      await deleteAccount();
                      alert('Ton compte a été supprimé. Au revoir et merci pour tout ! 🪶');
                      window.location.href = '/';
                    } catch (e) {
                      console.error(e);
                      alert('Erreur lors de la suppression. Réessaie ou contacte le support.');
                      setDeleting(false);
                    }
                  }}
                  whileTap={{ scale: 0.97 }}
                  disabled={deleting}
                >
                  {deleting ? 'Suppression en cours...' : 'Supprimer mon compte'}
                </motion.button>
              </div>

              <motion.button className="p-logout" onClick={logout} whileTap={{ scale: 0.97 }}>
                <IoLogOut /> Se déconnecter
              </motion.button>
            </motion.div>
          )}

          {/* ===== TAB MUR ===== */}
          {tab === 'mur' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <motion.button className="p-compose-btn" onClick={() => setShowCompose(!showCompose)} whileTap={{ scale: 0.97 }}>
                <IoPencil /> Écrire une publication
              </motion.button>

              <AnimatePresence>
                {showCompose && (
                  <motion.div className="p-compose" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <textarea className="p-compose-input" placeholder="Partage tes pensées avec tes abonnés..."
                      value={newPost} onChange={e => setNewPost(e.target.value)} maxLength={500} rows={3} />
                    <div className="p-compose-foot">
                      <span className="p-compose-count">{newPost.length}/500</span>
                      <button className="p-compose-send" onClick={publish} disabled={!newPost.trim() || posting}>
                        <IoSend /> {posting ? 'Envoi...' : 'Publier'}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="p-posts">
                {posts.length > 0 ? posts.map((post, i) => (
                  <motion.div key={post.id} className="p-post" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <div className="p-post-head">
                      <div className="p-post-avatar" style={{
                        background: post.authorAvatar ? `url(${post.authorAvatar}) center/cover no-repeat` : post.authorAvatarColor || 'var(--amber)'
                      }}>{!post.authorAvatar && (post.authorName || '?').charAt(0).toUpperCase()}</div>
                      <div className="p-post-meta"><strong>{post.authorName}</strong><span>{timeAgo(post.createdAt)}</span></div>
                      <button className="p-post-del" onClick={() => deletePost(post.id)}><IoTrash /></button>
                    </div>
                    <p className="p-post-text">{post.content}</p>
                    <div className="p-post-actions">
                      <span><IoHeart /> {post.likes || 0}</span>
                      <span><IoChatbubble /> {post.comments || 0}</span>
                    </div>
                  </motion.div>
                )) : (
                  <div className="p-posts-empty"><p>📝</p><p>Ton mur est vide. Partage quelque chose !</p></div>
                )}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}